import * as React from "react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Pencil, Ban, Flag } from "lucide-react";
import { cn } from "@/lib/utils";

import { WebComponents } from "@/interfaces";
import { KeywordSetEditor } from "./KeywordSetEditor";
import { useAccountSettings, useComplianceChecker } from "@/utils";
import { KeyphrasePolicy } from "@/services/bedrock";

interface OwnProps {
  policy: KeyphrasePolicy;
}

type Props = OwnProps;

export const KeywordSet: React.FC<Props> = ({ policy }) => {
  const [showEditor, setShowEditor] = React.useState<boolean>(false);
  const { hasWritePermission } = useAccountSettings();
  const withWritePermission = hasWritePermission(WebComponents.COMPLIANCE);
  const { blockedPhraseCount, flaggedPhraseCount, isLoading } = useComplianceChecker();
  const phraseCount = policy === "BLOCK" ? blockedPhraseCount : flaggedPhraseCount;
  const isBlocked = policy === "BLOCK";
  const title = isBlocked ? "Blocked Phrases" : "Flagged Phrases";
  const subtitle = isBlocked
    ? "If a post contains a blocked phrase in the caption, image, or video transcript, it will be blocked from being posted."
    : "If a post contains a flagged phrase in the caption, image, or video transcript, it will be flagged for review.";

  const IconComponent = isBlocked ? Ban : Flag;
  const iconBgClass = isBlocked
    ? "bg-red-50 text-red-600 border-red-200"
    : "bg-amber-50 text-amber-600 border-amber-200";

  const subCardClass = isBlocked
    ? "bg-red-50/20 border-red-100 hover:bg-red-50/40 hover:border-red-200/80 focus-visible:ring-2 focus-visible:ring-red-200"
    : "bg-amber-50/20 border-amber-100 hover:bg-amber-50/40 hover:border-amber-200/80 focus-visible:ring-2 focus-visible:ring-amber-200";

  const metricTextClass = isBlocked ? "text-red-600" : "text-amber-600";

  const pencilClass = isBlocked
    ? "bg-white text-red-600 border-red-200 group-hover/subcard:bg-red-600 group-hover/subcard:text-white group-hover/subcard:border-red-600"
    : "bg-white text-amber-600 border-amber-200 group-hover/subcard:bg-amber-500 group-hover/subcard:text-white group-hover/subcard:border-amber-500";

  return (
    <>
      <Card className="h-full border border-border bg-card shadow-sm rounded-xl">
        <Card.Content className="flex flex-row items-center justify-between gap-6 h-full">
          {/* Left Side: Icon, Title, Subtitle */}
          <div className="flex flex-col gap-2 items-start flex-1 min-w-0">
            <div className="flex flex-row gap-3 items-center">
              <div
                className={cn("p-1 rounded-lg border flex items-center justify-center shrink-0 shadow-xs", iconBgClass)}
              >
                <IconComponent className="size-3" />
              </div>
              <span className="text-lg font-semibold font-sans select-none text-foreground leading-none">{title}</span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed select-none">{subtitle}</p>
          </div>

          {/* Right Side: Clickable Metric Sub-card */}
          {withWritePermission ? (
            <button
              onClick={() => {
                if (!isLoading) setShowEditor(true);
              }}
              disabled={isLoading}
              className={cn(
                "flex items-center gap-3.5 py-1.5 px-3 rounded-xl border shrink-0 shadow-xs hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 text-left outline-none cursor-pointer select-none active:scale-[0.98] group/subcard",
                subCardClass,
                isLoading ? "opacity-60 cursor-not-allowed" : ""
              )}
            >
              <div className="flex flex-col items-center justify-center text-center select-none min-w-[64px]">
                {isLoading ? (
                  <Skeleton className="h-6 w-8 rounded-sm bg-slate-200/80" />
                ) : (
                  <span
                    className={cn(
                      "text-2xl font-extrabold tracking-tight font-sans leading-none transition-colors duration-200",
                      metricTextClass
                    )}
                  >
                    {phraseCount}
                  </span>
                )}
                <span className="text-[10px] font-bold text-muted-foreground font-sans uppercase tracking-wider mt-1.5 leading-none">
                  {phraseCount === 1 ? "phrase" : "phrases"}
                </span>
              </div>

              <div
                className={cn(
                  "shrink-0 h-8 w-8 flex items-center justify-center border transition-all duration-200 rounded-lg shadow-xs",
                  pencilClass
                )}
              >
                <Pencil className="size-4" />
              </div>
            </button>
          ) : (
            <div
              className={cn("flex items-center gap-3.5 py-1.5 px-3 rounded-xl border shrink-0 shadow-xs", subCardClass)}
            >
              <div className="flex flex-col items-center justify-center text-center select-none min-w-[64px]">
                {isLoading ? (
                  <Skeleton className="h-6 w-8 rounded-sm bg-slate-200/80" />
                ) : (
                  <span
                    className={cn("text-2xl font-extrabold tracking-tight font-sans leading-none", metricTextClass)}
                  >
                    {phraseCount}
                  </span>
                )}
                <span className="text-[10px] font-bold text-muted-foreground font-sans uppercase tracking-wider mt-1.5 leading-none">
                  {phraseCount === 1 ? "phrase" : "phrases"}
                </span>
              </div>
            </div>
          )}
        </Card.Content>
      </Card>

      <KeywordSetEditor
        open={showEditor}
        onClose={() => setShowEditor(false)}
        policy={policy}
        onUpdate={() => {
          setShowEditor(false);
        }}
      />
    </>
  );
};
